import { ArrowUpRight, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { Link } from "wouter";
import {
  ASSETS,
  CITY,
  CTA_TEXT,
  EMAIL,
  NAV_LINKS,
  SLOGAN,
  SOCIAL_LINKS,
  WHATSAPP_NUMBER_DISPLAY,
} from "@/lib/brand";
import { openWhatsApp } from "@/components/WhatsAppLeadModal";
import { STORAGE_MEDIA_ORIGIN } from "@/lib/mediaRecovery";

const SOCIAL_ICONS = {
  Instagram: Instagram,
  Facebook: Facebook,
  LinkedIn: Linkedin,
  YouTube: Youtube,
} as const;

export default function Footer() {
  return (
    <footer className="bg-charcoal text-offwhite" role="contentinfo">

      {/* colunas principais */}
      <div className="container grid gap-x-8 gap-y-10 pt-16 pb-14 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[1.4fr_1fr_1fr_1.1fr_1fr_1fr]">
        <div>
          {/* Logo + slogan */}
          <img
            src={ASSETS.logoWhite}
            alt="EquipaPRO"
            width={200}
            height={32}
            loading="lazy"
            className="h-7 w-auto mb-3"
          />
          <p className="mb-4 max-w-xs text-sm leading-relaxed text-offwhite/60">
            {SLOGAN}
          </p>
          {/* Normas ABNT + Selo de garantia */}
          <div className="mt-5 flex flex-wrap items-start gap-4">
            <div>
              <p className="text-sm font-bold text-offwhite/90 mb-2">Produtos de acordo com as normas:</p>
              <ul className="space-y-1">
                <li className="text-sm font-extrabold text-offwhite/80">ABNT NBR 15202:2006</li>
                <li className="text-sm font-extrabold text-offwhite/80">ABNT NBR 16025:2012</li>
              </ul>
            </div>
            <div className="flex items-center gap-2">
              <img
                src={`https://equipapro-zblaoftc.manus.space/manus-storage/selo-3anos-v2_ddbcbdd4_b4bfbddf.png`}
                alt="Selo de 3 anos de garantia"
                width={80}
                height={80}
                loading="lazy"
                className="h-20 w-20 object-contain drop-shadow-lg"
              />
              <img
                src={`https://equipapro-zblaoftc.manus.space/manus-storage/selo-2anos-v2_0d16bb7e_1f769213.png`}
                alt="Selo de 2 anos de garantia"
                width={80}
                height={80}
                loading="lazy"
                className="h-20 w-20 object-contain drop-shadow-lg"
              />
            </div>

          </div>
        </div>

        <nav aria-label="Links rápidos">
          <h2 className="font-display text-sm uppercase tracking-[0.18em] text-warmgold">
            Links rápidos
          </h2>
          <ul className="mt-4 space-y-2.5">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                  {link.label}
                  <ArrowUpRight
                    className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
            <li>
              <Link
                href="/faq"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                FAQ
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
          </ul>
        </nav>

        <div>
          <h2 className="font-display text-sm uppercase tracking-[0.18em] text-warmgold">
            Contato
          </h2>
          <ul className="mt-4 space-y-3 text-sm text-offwhite/70">
            <li>
              <button
                type="button"
                onClick={() => openWhatsApp({ message: "Olá! Gostaria de falar com a equipe da EquipaPRO.", channel: "comercial", origin: "footer" })}
                className="inline-flex items-center gap-2.5 transition-colors hover:text-warmgold">
                <Phone className="h-4 w-4 shrink-0 text-warmgold" aria-hidden="true" />
                {WHATSAPP_NUMBER_DISPLAY}
              </button>
            </li>
            <li>
              <a
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center gap-2.5 transition-colors hover:text-warmgold">
                <Mail className="h-4 w-4 shrink-0 text-warmgold" aria-hidden="true" />
                {EMAIL}
              </a>
            </li>
            <li className="inline-flex items-center gap-2.5">
              <MapPin className="h-4 w-4 shrink-0 text-warmgold" aria-hidden="true" />
              {CITY}
            </li>
          </ul>
        </div>

        <nav aria-label="Links úteis">
          <h2 className="font-display text-sm uppercase tracking-[0.18em] text-warmgold">
            Links úteis
          </h2>
          <ul className="mt-4 space-y-2.5">
            <li>
              <Link
                href="/politica-de-privacidade"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                Política de Privacidade
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                href="/termos-de-uso"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                Termos de Uso
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                href="/politica-de-devolucao"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                Política de Devolução
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                href="/parceiros"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                Seja Parceiro
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                href="/trabalhe-conosco"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                Trabalhe Conosco
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <Link
                href="/faq"
                className="group inline-flex items-center gap-1.5 text-sm text-offwhite/70 transition-colors hover:text-warmgold">
                Perguntas Frequentes
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
              </Link>
            </li>
          </ul>
          {/* Selos de conformidade recuperados da versão anterior. */}
          <div className="mt-5 flex items-center gap-3">
            <Link
              href="/sobre#h-politicas"
              className="group relative flex flex-col items-center"
              title="Compliance EquipaPRO — ver políticas"
            >
              <img
                src={`https://equipapro-zblaoftc.manus.space/manus-storage/compliance-icon_0fa9340d_3a931dfb.png`}
                alt="Compliance EquipaPRO"
                width={56}
                height={56}
                loading="lazy"
                className="h-14 w-14 object-contain opacity-80 transition-all duration-200 ease-out group-hover:scale-110 group-hover:opacity-100 group-hover:drop-shadow-[0_0_8px_rgba(255,165,0,0.5)]"
              />
              <span className="pointer-events-none absolute bottom-full mb-2 whitespace-nowrap rounded bg-charcoal px-2 py-1 text-xs text-offwhite opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
                Compliance
              </span>
            </Link>
            <Link
              href="/sobre#h-rohs"
              className="group relative flex flex-col items-center"
              title="RoHS Compliant — Conforme Diretriz 2011/65/EU"
            >
              <img
                src={`https://equipapro-zblaoftc.manus.space/manus-storage/rohs-icon-fixed_4ebc414a_e69d21f4.png`}
                alt="RoHS Compliant — Conforme Diretriz 2011/65/EU"
                width={56}
                height={56}
                loading="lazy"
                className="h-14 w-14 object-contain opacity-80 transition-all duration-200 ease-out group-hover:scale-110 group-hover:opacity-100 group-hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]"
              />
              <span className="pointer-events-none absolute bottom-full mb-2 whitespace-nowrap rounded bg-charcoal px-2 py-1 text-xs text-offwhite opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
                RoHS Compliant
              </span>
            </Link>
          </div>
        </nav>

        <div>
          <h2 className="font-display text-sm uppercase tracking-[0.18em] text-warmgold">
            Horário de funcionamento
          </h2>
          <ul className="mt-4 space-y-3 text-sm text-offwhite/70">
            <li>
              <p className="font-medium text-offwhite/90">Segunda a Quinta-feira</p>
              <p>das 08:00 às 18:00h</p>
            </li>
            <li>
              <p className="font-medium text-offwhite/90">Sexta-feira</p>
              <p>das 08:00 às 17:00h</p>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-sm uppercase tracking-[0.18em] text-warmgold">
            Redes sociais
          </h2>
          <ul className="mt-4 flex items-center gap-3">
            {SOCIAL_LINKS.map((social) => {
              const Icon = SOCIAL_ICONS[social.label];
              return (
                <li key={social.label}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`EquipaPRO no ${social.label}`}
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-offwhite/15 text-offwhite/70 transition-colors duration-150 ease-out hover:border-warmgold hover:text-warmgold">
                    <Icon className="h-4.5 w-4.5" strokeWidth={1.8} aria-hidden="true" />
                  </a>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 max-w-[12rem] text-xs leading-relaxed text-offwhite/45">
            Acompanhe projetos, lançamentos e bastidores da fábrica.
          </p>
        </div>
      </div>

      {/* barra final */}
      <div className="border-t border-offwhite/10">
        <div className="container flex flex-col items-center justify-between gap-2 py-5 text-xs text-offwhite/45 sm:flex-row">
          <p>
            © {new Date().getFullYear()} EquipaPRO — Portas e Controle de
            Acesso. Todos os direitos reservados.
          </p>
          <p>{CITY} · Atendimento em todo o Brasil</p>
        </div>
      </div>
    </footer>
  );
}
